/**
 * API Keys Management
 * Developer portal for managing API access
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Clock,
  Activity,
  Shield,
  AlertTriangle,
  Eye,
  EyeOff,
} from "lucide-react";

export default function ApiKeys() {
  return (
    <DashboardLayout>
      <ApiKeysContent />
    </DashboardLayout>
  );
}

function ApiKeysContent() {
  const utils = trpc.useUtils();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [newKeyData, setNewKeyData] = useState<{
    id: number;
    name: string;
    key: string;
  } | null>(null);

  // Get API keys
  const { data: keys, isLoading } = trpc.api.listKeys.useQuery();

  // Revoke mutation
  const revokeMutation = trpc.api.revokeKey.useMutation({
    onSuccess: () => {
      utils.api.listKeys.invalidate();
      setRevokeDialogOpen(false);
      setSelectedKey(null);
    },
  });

  const handleRevokeClick = (key: { id: number; name: string }) => {
    setSelectedKey(key);
    setRevokeDialogOpen(true);
  };

  const activeKeys = keys?.filter(k => k.isActive) || [];
  const revokedKeys = keys?.filter(k => !k.isActive) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Keys</h1>
          <p className="text-muted-foreground">
            Manage API keys for programmatic access to your data
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create API Key
            </Button>
          </DialogTrigger>
          <CreateKeyDialog
            onSuccess={data => {
              setNewKeyData(data);
              setCreateDialogOpen(false);
              utils.api.listKeys.invalidate();
            }}
          />
        </Dialog>
      </div>

      {/* New Key Alert */}
      {newKeyData && (
        <NewKeyAlert
          keyData={newKeyData}
          onDismiss={() => setNewKeyData(null)}
        />
      )}

      {/* Active Keys */}
      <Card>
        <CardHeader>
          <CardTitle>Active Keys</CardTitle>
          <CardDescription>
            API keys that are currently active and can be used for
            authentication
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="animate-pulse text-muted-foreground">
                Loading...
              </div>
            </div>
          ) : activeKeys.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Rate Limit</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeKeys.map(key => (
                  <TableRow key={key.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{key.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {key.maskedKey}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(key.scopes as string[])?.slice(0, 2).map(scope => (
                          <Badge
                            key={scope}
                            variant="outline"
                            className="text-xs"
                          >
                            {scope.split(":")[1]}
                          </Badge>
                        ))}
                        {(key.scopes as string[])?.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{(key.scopes as string[]).length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{key.rateLimit}/hr</span>
                    </TableCell>
                    <TableCell>
                      {key.lastUsedAt ? (
                        <span className="text-sm text-muted-foreground">
                          {new Date(key.lastUsedAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Never
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {key.expiresAt ? (
                        <span className="text-sm text-muted-foreground">
                          {new Date(key.expiresAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Never
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleRevokeClick({ id: key.id, name: key.name })
                        }
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Key className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No API keys yet</h3>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                Create an API key to start integrating with the platform
                programmatically.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revoked Keys */}
      {revokedKeys.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Revoked Keys</CardTitle>
            <CardDescription>
              Keys that have been revoked and can no longer be used
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Revoked</TableHead>
                  <TableHead>Total Requests</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revokedKeys.map(key => (
                  <TableRow key={key.id} className="opacity-60">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{key.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {key.maskedKey}
                      </code>
                    </TableCell>
                    <TableCell>
                      {key.revokedAt && (
                        <span className="text-sm text-muted-foreground">
                          {new Date(key.revokedAt).toLocaleDateString()}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{key.requestCount || 0}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Revoke Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently revoke the key "{selectedKey?.name}". Any
              applications using this key will immediately lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (selectedKey) {
                  revokeMutation.mutate({ keyId: selectedKey.id });
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke Key"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateKeyDialog({
  onSuccess,
}: {
  onSuccess: (data: { id: number; name: string; key: string }) => void;
}) {
  const [name, setName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [rateLimit, setRateLimit] = useState("1000");
  const [expiresIn, setExpiresIn] = useState<string>("");

  // Get available scopes
  const { data: scopes } = trpc.api.listScopes.useQuery();

  // Create mutation
  const createMutation = trpc.api.createKey.useMutation({
    onSuccess: data => {
      onSuccess({ id: data.id, name: data.name, key: data.key });
    },
  });

  const handleCreate = () => {
    createMutation.mutate({
      name,
      scopes: selectedScopes as any,
      rateLimit: parseInt(rateLimit),
      expiresInDays: expiresIn ? parseInt(expiresIn) : undefined,
    });
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogDescription>
          Generate a new API key for programmatic access
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Key Name</Label>
          <Input
            id="name"
            placeholder="e.g., Production Backend"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>

        {/* Scopes */}
        <div className="space-y-2">
          <Label>Permissions</Label>
          <div className="grid grid-cols-2 gap-2">
            {scopes?.map(scope => (
              <div
                key={scope.scope}
                className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedScopes.includes(scope.scope)
                    ? "border-primary bg-primary/5"
                    : "hover:bg-muted"
                }`}
                onClick={() => toggleScope(scope.scope)}
              >
                <Checkbox checked={selectedScopes.includes(scope.scope)} />
                <div>
                  <div className="text-sm font-medium">{scope.scope}</div>
                  <div className="text-xs text-muted-foreground">
                    {scope.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Rate Limit */}
        <div className="space-y-2">
          <Label htmlFor="rateLimit">Rate Limit (requests/hour)</Label>
          <Select value={rateLimit} onValueChange={setRateLimit}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="100">100 req/hr</SelectItem>
              <SelectItem value="500">500 req/hr</SelectItem>
              <SelectItem value="1000">1,000 req/hr</SelectItem>
              <SelectItem value="5000">5,000 req/hr</SelectItem>
              <SelectItem value="10000">10,000 req/hr</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Expiration */}
        <div className="space-y-2">
          <Label htmlFor="expires">Expiration (optional)</Label>
          <Select
            value={expiresIn || "__never__"}
            onValueChange={value =>
              setExpiresIn(value === "__never__" ? "" : value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Never expires" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__never__">Never expires</SelectItem>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="365">1 year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={handleCreate}
          disabled={
            !name || selectedScopes.length === 0 || createMutation.isPending
          }
        >
          {createMutation.isPending ? "Creating..." : "Create Key"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function NewKeyAlert({
  keyData,
  onDismiss,
}: {
  keyData: { id: number; name: string; key: string };
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(keyData.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="border-[var(--warning)] bg-[var(--warning)]/5">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-[var(--warning)] flex-shrink-0 mt-0.5" />
          <div className="flex-1 space-y-3">
            <div>
              <h4 className="font-semibold text-[var(--warning)]">
                Copy Your API Key Now
              </h4>
              <p className="text-sm text-muted-foreground">
                This is the only time you'll see this key. Store it securely.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm bg-background px-3 py-2 rounded border font-mono">
                {visible
                  ? keyData.key
                  : keyData.key.replace(
                      /(?<=zhtax_[a-f0-9]{8}_).+/,
                      "*".repeat(32)
                    )}
              </code>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setVisible(!visible)}
              >
                {visible ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
              <Button variant="outline" size="icon" onClick={handleCopy}>
                {copied ? (
                  <Check className="h-4 w-4 text-[var(--success)]" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={onDismiss}>
              I've copied the key
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
